document.addEventListener('alpine:init', () => {
    Alpine.data('joinOnline', (clubsAndPlans, addressApiKey, veriffApiKey, adyenClientKey, memberDisclaimerCheckboxes, directDebitContent) => ({
        // #region Properties
        addressApiKey: addressApiKey,
        veriffApiKey: veriffApiKey,
        adyenClientKey: adyenClientKey,
        directDebitContent: directDebitContent,

        skipVeriff: false,
        skipSteps: [],
        currentStep: 1,
        totalSteps: 9,
        loading: false,

        clubsAndPlans: clubsAndPlans,
        allPlans: clubsAndPlans.map(club => club.plans).flat(),
        addOns: [],
        planPrice: null,
        payNowAmount: null,
        recurringAmount: null,
        billingStartDate: null,

        // The selected club
        selectedClubId: null,
        selectedClub: null,
        // The plans available for the selected club
        selectedClubPlans: [],
        // The selected plan
        selectedPlanId: null,
        selectedPlan: null,

        // turn selectedPlanId into get/set
        internalSelectedPlanId: null,

        // The selected add-ons
        selectedAddonIds: [],

        // Membership start date
        startDate: null,

        // Member details
        firstName: '',
        lastName: '',
        title: '',
        dob: '',
        addressLookupComplete: false,
        address1: '',
        address2: '',
        postcode: '',
        email: '',
        mobile: '',
        statements: [],

        memberDetailsForm: {
            pristine: true,
            fields: {
                title: {
                    required: true,
                    pattern: null,
                    message: 'Please enter your title',
                    pristine: true,
                    group: false
                },
                firstName: {
                    required: true,
                    pattern: null,
                    message: 'Please enter your first name',
                    pristine: true,
                    group: false
                },
                lastName: {
                    required: true,
                    pattern: null,
                    message: 'Please enter your last name',
                    pristine: true,
                    group: false
                },
                dob: {
                    required: true,
                    pattern: null,
                    message: 'Please enter your date of birth',
                    pristine: true,
                    group: false
                },
                address1: {
                    required: true,
                    pattern: null,
                    message: 'Please enter your address',
                    pristine: true,
                    group: false
                },
                postcode: {
                    required: true,
                    pattern: /^(GIR 0AA|[A-Z]{1,2}\d{1,2} ?\d[A-Z]{2}|[A-Z]{1,2}\d[A-Z] ?\d[A-Z]{2})$/i,
                    message: 'Please enter your postcode',
                    pristine: true,
                    group: false
                },
                email: {
                    required: true,
                    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                    pristine: true,
                    group: false
                },
                mobile: {
                    required: true,
                    pattern: /^\+((9[976]\d{1,2}|8[987530]\d{1,2}|6[987]\d{1,2}|5[90]\d{1,2}|42\d{1}|3[875]\d{1,2}|2[98654321]\d{1,2}|9[8543210]\d{1}|8[6421]\d{1}|6[6543210]\d{1}|5[87654321]\d{1}|4[987654310]\d{1}|3[9643210]\d{1}|2[70]\d{1}|7\d{1}|1))\d{6,12}$|^(07\d{9})$/,
                    message: 'Please enter a valid mobile number',
                    pristine: true,
                    group: false
                },
                privacy: {
                    required: true,
                    pattern: null,
                    message: 'Please agree to the confirmation statements',
                    pristine: true,
                    group: true,
                    groupMembers: {},
                },
            },
        },

        // Did email validation via Circuit API fail?
        emailValidationFailed: false,

        // Has the verification step finished?
        veriffFinished: false,

        // Bank account details
        sortCode: null,
        accountNumber: null,
        accountName: null,
        accountHolder: false,

        bankAccountForm: {
            pristine: true,
            fields: {
                sortCode: {
                    required: true,
                    pattern: /^\d{2}-\d{2}-\d{2}$/,
                    message: 'Please enter a valid sort code',
                    pristine: true,
                    group: false
                },
                accountNumber: {
                    required: true,
                    pattern: /^\d{6,10}$/,
                    message: 'Please enter a valid account number',
                    pristine: true,
                    group: false
                },
                accountName: {
                    required: true,
                    pattern: /^[a-zA-Z\s\-']+$/,
                    message: 'Please enter a valid account name',
                    pristine: true,
                    group: false
                },
                accountHolder: {
                    required: true,
                    pattern: null,
                    message: 'Please agree to the confirmation statement',
                    pristine: true,
                    group: false
                },
            },
        },

        bankDirectDebitAccountInvalid: false,
        tempMemberId: null,
        membershipNumber: null,

        // #endregion

        // #region Functions

        trackStep(stepNumber, stepDescription) {
            if (typeof gtag === 'function') {
                gtag("event", "JoinOnline", {
                    step: `Step ${stepNumber} - ${stepDescription}`
                });
            } else {
                console.log("Google Analytics event not started");
            }
        },

        /**
        * Initialize checkboxes for disclaimers and required statements
        */
        initializeCheckboxes() {
            this.memberDisclaimerCheckboxes = memberDisclaimerCheckboxes;

            this.statements = this.memberDisclaimerCheckboxes.map(checkbox => checkbox.required_status ? false : true);

            this.memberDetailsForm.fields.privacy.groupMembers = {};
            this.memberDisclaimerCheckboxes.forEach((checkbox, index) => {
                this.memberDetailsForm.fields.privacy.groupMembers[`statement${index + 1}`] = {
                    required: checkbox.required_status
                };
            });

            this.statements = new Array(this.memberDisclaimerCheckboxes.length).fill(false);
        },

        init() {
            this.initializeCheckboxes();

            // Initialize Flatpickr & getAddress
            this.$nextTick(() => {
                flatpickr("#dob", {
                    altInput: true,
                    altFormat: "F j, Y",
                    dateFormat: "Y-m-d",
                    maxDate: new Date().setFullYear(new Date().getFullYear() - 18), // Restrict to 18 years and above
                    onChange: (selectedDates, dateStr) => {
                        this.dob = dateStr;
                    }
                });

                getAddress.autocomplete(
                    'address_lookup',
                    this.addressApiKey
                );

                document.addEventListener("getaddress-autocomplete-address-selected", (e) => {
                    const formattedAddress = e.address.formatted_address;
                    this.address1 = formattedAddress.slice(0, 3).filter(Boolean).join(', ');
                    this.address2 = formattedAddress[3];
                    this.postcode = e.address.postcode;
                    document.getElementById('address_lookup').value = '';
                    this.handleFieldChange('memberDetailsForm', 'address1');
                    this.handleFieldChange('memberDetailsForm', 'postcode');
                    this.addressLookupComplete = true;
                });
            });
        },
        /** Initialise the flatpickr for startDate - start with tomorrow's date
         * 
         * @returns {void}
         */
        initStartDate() {
            // default behaviour for minimum start date - tomorrow
            let minimumStartDate = new Date();
            minimumStartDate.setDate(minimumStartDate.getDate() + 1);

            // if the selected club has a start date (set in General Settings)
            // use that as the minimum start date
            const selectedClubStartDate = this.selectedClub.startDate;

            if (selectedClubStartDate) {
                const clubStartDate = new Date(selectedClubStartDate);
                // only if the date is in the future
                if (clubStartDate > minimumStartDate) {
                    minimumStartDate = clubStartDate;
                    console.log('Updated minimum start date to club start date:', minimumStartDate);
                }
            }

            this.startDate = minimumStartDate.toISOString().split('T')[0];

            flatpickr("#startDate", {
                altInput: true,
                altFormat: "F j, Y",
                dateFormat: "Y-m-d",
                defaultDate: minimumStartDate,
                minDate: minimumStartDate, // Restrict to tomorrow and above
                onChange: (selectedDates, dateStr) => {
                    this.startDate = dateStr;
                }
            });
        },
        /**
         * Initialise the Veriff SDK
         * 
         * @param {string} firstName 
         * @param {string} lastName 
         */
        initVeriff(firstName, lastName) {
            const veriff = Veriff({
                apiKey: this.veriffApiKey,
                parentId: 'veriff-root',
                onSession: (err, response) => {
                    window.veriffSDK.createVeriffFrame({
                        url: response.verification.url,
                        onEvent: (msg) => {
                            if (msg === "FINISHED") {
                                document.getElementById('veriff-root').style.display = 'none';
                                this.veriffFinished = true;
                            }
                        }
                    });
                }
            });

            veriff.setParams({
                vendorData: ' '
            });

            veriff.mount({
                formLabel: {
                    givenName: 'First name',
                    lastName: 'Last name',
                },
            });

            const root = document.getElementById('veriff-root');
            const container = root.querySelector('.veriff-container');
            container.classList.add('flex', 'flex-col', 'items-center', 'justify-center');

            const inputs = root.querySelectorAll('input:not([type="submit"])');
            inputs.forEach(input => {
                input.classList.remove('veriff-text');
                input.classList.add('w-full');
            });

            const el = document.getElementById('veriff-submit-btn');
            const classesToRemove = Array.from(el.classList);
            classesToRemove.forEach(className => {
                el.classList.remove(className);
            });
            el.classList.add('btn-primary', 'light', 'w-full', 'my-12', 'cursor-pointer');

            const description = root.querySelector('.veriff-description');
            description.classList.add('bg-dark-01', '!text-white', 'rounded-md', 'p-4');

            const givenName = root.querySelector('#veriff-given-name');
            givenName.value = firstName;
            const lastNameField = root.querySelector('#veriff-last-name');
            lastNameField.value = lastName;
        },
        /**
         * Move to the next step
         */
        nextStep() {
            // if skipVeriff is true and current step is 5, skip to step 7
            // used during development & testing to skip the Veriff step
            const increment = this.skipVeriff && this.currentStep === 5 ? 2 : 1;
            this.currentStep += increment;

            // if skipSteps includes the current step, skip to the next step
            // used to jump over certain steps when they are not needed
            // i.e., when there are no add-ons
            if (this.skipSteps.includes(this.currentStep)) {
                this.currentStep++;
            }

            window.scrollTo(0, 0);

            const stepDescriptions = {
                1: "Your Home Club",
                2: "Your membership",
                3: "Your details",
                4: "Review Personal Details & Selection",
                5: "Enter Direct Debit information",
                7: "Initial Payment",
                8: "Membership Confirmation"
            };
            const stepDescription = stepDescriptions[this.currentStep];
            if (stepDescription) {
                this.trackStep(this.currentStep, stepDescription);
            }

            return;

            // unfinished implementation of sliding between steps
            // if (this.currentStep < this.totalSteps) {
            //     const step = document.getElementById('step' + this.currentStep);
            //     const nextStep = document.getElementById('step' + (this.currentStep + increment));

            //     step.style.transform = 'translateY(-100%)';
            //     step.style.opacity = 0;

            //     setTimeout(() => {
            //         this.currentStep = this.currentStep + increment;
            //         nextStep.style.opacity = 1;
            //         nextStep.style.display = 'flex';

            //         step.style.display = 'none';
            //         step.style.transform = 'translateY(0)';
            //     }, 400);

            //     // // scroll to top
            //     window.scrollTo(0, 0);
            // }
        },
        /**
         * Move to the previous step
         */
        prevStep() {
            const increment = this.skipVeriff && this.currentStep === 7 ? 2 : 1;
            this.currentStep = this.currentStep - increment;

            // if skipSteps includes the current step, skip to the next step
            // used to jump over certain steps when they are not needed
            // i.e., when there are no add-ons
            if (this.skipSteps.includes(this.currentStep)) {
                this.currentStep--;
            }

            window.scrollTo(0, 0);
            return;

            // unfinished implementation of sliding between steps
            // if (this.currentStep > 1) {
            //     const step = document.getElementById('step' + this.currentStep);
            //     const prevStep = document.getElementById('step' + (this.currentStep - 1));

            //     step.style.transform = 'translateY(100%)';
            //     step.style.opacity = 0;

            //     setTimeout(() => {
            //         this.currentStep--;
            //         prevStep.style.opacity = 1;
            //         prevStep.style.display = 'flex';

            //         step.style.display = 'none';
            //         step.style.transform = 'translateY(0)';
            //     }, 400);

            //     // // scroll to top
            //     window.scrollTo(0, 0);
            // }
        },
        /**
         * Handle the selected club change & reset the selected plan
         * 
         * @returns {Promise}
         */
        async handleSelectedClubChange() {
            this.selectedPlanId = null;
            this.selectedPlan = null;
            if (!this.selectedClubId) {
                this.selectedClubPlans = [];
                return;
            }
            const selectedClub = this.clubsAndPlans.find(club => club.id === +this.selectedClubId);
            if (selectedClub && selectedClub.plans) {
                console.log('Selected club: ', selectedClub);
                this.selectedClub = selectedClub;
                this.selectedClubPlans = selectedClub.plans;

                this.initStartDate();

                await this.getAddOns();
            } else {
                console.error('Selected club not found');
                this.selectedClubPlans = [];
            }
        },
        /**
         * Determine if the given plan is selected
         * 
         * @param {object} plan
         * @returns {boolean}
         */
        isSelectedPlan(plan) {
            return this.selectedPlanId == plan.id;
        },
        /**
         * Determine if the given add-on is selected
         * 
         * @param {object} addon
         * @returns {boolean}
         */
        isSelectedAddon(addon) {
            return this.selectedAddonIds.includes(addon.id);
        },
        /**
         * Toggle the selected state of the given add-on
         * 
         * @param {object} addon
         * @returns {void}
         */
        toggleSelectedAddon(addon) {
            if (addon.id === 0) {
                this.selectedAddonIds = [0];
            }
            else if (this.selectedAddonIds.includes(0)) {
                this.selectedAddonIds = [addon.id];
            }
            else if (this.selectedAddonIds.includes(addon.id)) {
                this.selectedAddonIds = this.selectedAddonIds.filter(id => id !== addon.id);
                if (this.selectedAddonIds.length === 0) {
                    this.selectedAddonIds = [0];
                }
            } else {
                this.selectedAddonIds.push(addon.id);
            }
        },
        /**
         * Handle the selected plan change
         * 
         * @returns {void}
         */
        handleSelectedPlanChange() {
            if (!this.selectedPlanId) {
                return;
            }
            const selectedPlan = this.allPlans.find(plan => plan.id === +this.selectedPlanId);
            if (selectedPlan) {
                this.selectedPlan = selectedPlan;
                console.log('Selected plan: ', selectedPlan);
            }
        },
        /**
         * Get the add-ons for the selected club and start date
         * 
         * @returns {Promise}
         */
        async getAddOns() {
            this.addOns = [];
            const data = {
                clubId: this.selectedClubId,
                startDate: this.startDate,
            };

            this.loading = true;

            return fetch(`${ajaxurl}?action=getAddOns`, {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(data => {
                console.log('Add-ons: ', data);
                if (data.success) {
                    if (data.addOns.length > 0) {
                        this.addOns = data.addOns;
                        this.addOns.push({
                            id: 0,
                            name: 'None',
                            description: 'Continue without any add-ons',
                            price: 0,
                        });
                        this.selectedAddonIds = [0];
                    }
                    return data;
                } else {
                    console.error('Error getting add-ons: ', data.message);
                }
            }).finally(() => {
                if (this.addOns.length === 0) {
                    console.log('No add-ons available, skipping step');
                    this.skipSteps.push(3);
                } else {
                    this.skipSteps = this.skipSteps.filter(step => step !== 3);
                }
                this.loading = false;
            });
        },
        /**
         * Create a new member
         * 
         * @returns {Promise}
         */
        async createMember() {
            const planData = {
                clubId: this.selectedClubId,
                planId: this.selectedPlanId,
                addonIds: this.selectedAddonIds,
                planType: this.selectedPlan.isRecurring ? 'PM' : 'PL',
                startDate: this.startDate,
            };

            console.log('Selected plan: ', this.selectedPlan);

            const memberData = {
                title: this.title,
                firstName: this.firstName,
                lastName: this.lastName,
                dob: this.dob,
                address1: this.address1,
                address2: this.address2,
                postcode: this.postcode,
                email: this.email,
                mobile: this.mobile,
            };

            const directDebitData = {
                sortCode: this.sortCode.replace(/-/g, ''),
                accountNumber: this.accountNumber,
                accountName: this.accountName,
            };

            const data = {
                planData,
                memberData,
                directDebitData,
            };

            if (this.tempMemberId) {
                console.log('Using temp member id: ', this.tempMemberId);
                data.tempMemberId = this.tempMemberId;
            } else {
                console.log('No temp member id, this is a new member');
            }

            this.loading = true;

            return fetch(`${ajaxurl}?action=createMember`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(data => {
                if (data.success) {
                    if (data.result && data.result.id) {
                        this.tempMemberId = data.result.id;
                        console.log('Member created with id: ', this.tempMemberId);
                    } else {
                        // TODO let the user know that the member was not created
                        console.log('Member not created');
                    }
                } else {
                    throw new Error(`Error creating member: ${data.message}`);
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        /**
         * Confirm a member - turns a temp member into a full member
         * 
         * @returns {Promise}
         */
        async confirmMember() {
            const data = {
                memberId: this.tempMemberId,
            };

            this.loading = true;

            return fetch(`${ajaxurl}?action=confirmMember`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(data => {
                if (data.success) {
                    if (data.result && data.result.membershipNumber) {
                        console.log('Member confirmed with id: ', data.result.membershipNumber);
                        this.membershipNumber = data.result.membershipNumber;
                        this.nextStep();
                    } else {
                        // TODO let the user know that the member was not confirmed
                        console.log('Member not confirmed');
                    }
                } else {
                    throw new Error(`Error confirming member: ${data.message}`);
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        /**
         * Validate the provided email address - get the plan price & move to the next step if valid
         */
        async processMemberDetails() {
            // validate the email address with fetch validateEmailAddress and if the isSuccess is true, get the plan price
            const valid = this.isFormValid('memberDetailsForm');
            if (!valid) {
                this.addressLookupComplete = true;
                return;
            }

            this.emailValidationFailed = false;
            this.loading = true;

            const data = {
                email: this.email,
            };

            return fetch(`${ajaxurl}?action=validateEmailAddress`, {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(async data => {
                if (data.success) {
                    if (data.result.isSuccess) {
                        await this.getPlanPrice();
                        this.nextStep();
                    } else {
                        this.emailValidationFailed = true;
                    }
                } else {
                    throw new Error(`Error validating email address`);
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        /**
         * Get the plan price based on the selected club, plan, start date & add-ons
         * 
         * @returns {Promise}
         * @throws {Error}
         */
        async getPlanPrice() {
            const valid = this.isFormValid('memberDetailsForm');
            if (!valid) {
                return;
            }

            this.loading = true;

            const data = {
                clubId: this.selectedClubId,
                planId: this.selectedPlanId,
                startDate: this.startDate,
                addOnIds: this.selectedAddonIds,
            };

            return fetch(`${ajaxurl}?action=getPlanPrice`, {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return new Promise(resolve => setTimeout(() => resolve(response.json()), 200));
            }).then(data => {
                if (data.success) {
                    this.planPrice = data.planPrice;
                    this.payNowAmount = this.planPrice.paynow.amount;
                    this.recurringAmount = Math.round(this.planPrice.recurring.amount * 100) / 100;
                    this.billingStartDate = this.planPrice.firstBilling.startDate;

                    console.log('Plan price: ', this.planPrice);
                    console.log('Pay now amount: ', this.planPrice.paynow);
                    this.initAdyenCheckout(this.planPrice.paynow.amount * 100);
                    return data;
                } else {
                    throw new Error(`Error getting add ons: ${data.message}`);
                }
            });
        },
        /**
         * Return the user to the member details step
         */
        editMemberDetails() {
            this.currentStep = 4;
            scrollTo(0, 0);
        },
        /**
         * Return the user to the club selection step
         */
        editClubDetails() {
            this.currentStep = 1;
            scrollTo(0, 0);
        },
        /**
         * Return the user to the plan selection step
         */
        editPlanDetails() {
            this.currentStep = 2;
            scrollTo(0, 0);
        },
        /**
         * Return the user to the add-on selection step
         */
        editAddonDetails() {
            this.currentStep = 3;
            scrollTo(0, 0);
        },
        /**
         * Initialise the Veriff SDK & move to the next step
         */
        confirmPlanPrices() {
            this.initVeriff(this.firstName, this.lastName);
            this.nextStep();
        },
        /**
         * Validate the bank account details & create the member if valid
         * 
         * @returns {Promise}
         * @throws {Error}
         */
        async validateBankAccount() {
            if (!this.isFormValid('bankAccountForm')) {
                return;
            }

            this.loading = true;

            const data = {
                sortCode: this.sortCode,
                accountNumber: this.accountNumber,
            };

            return fetch(`${ajaxurl}?action=validateBankAccount`, {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(async data => {
                if (data.success) {
                    if (data.result && data.result.Valid == "Invalid" && !data.result.AcceptsDirectDebitTransactions) {
                        this.bankDirectDebitAccountInvalid = true;
                        setTimeout(() => {
                            this.bankDirectDebitAccountInvalid = false;
                        }, 3000);

                        throw new Error(`Error validating bank account`);
                    } else {
                        this.bankDirectDebitAccountInvalid = false;

                        await this.createMember();
                        this.nextStep();
                    }
                } else {
                    this.bankDirectDebitAccountInvalid = true;
                    setTimeout(() => {
                        this.bankDirectDebitAccountInvalid = false;
                    }, 3000);
                    throw new Error(`Error validating bank account`);
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        /**
         * Validate the form
         * 
         * @param {string} formName
         * @returns {boolean}
         */
        isFormValid(formName) {
            Object.keys(this[formName].fields).forEach(fieldName => {
                this[formName].fields[fieldName].pristine = false;
            });
            return Object.keys(this[formName].fields).every(fieldName => this.validateField(formName, fieldName));
        },
        /**
         * Get the error message for the given field in the given form
         * 
         * @param {string} formName
         * @param {string} fieldName
         * @returns {string|boolean}
         */
        getErrorMessage(formName, fieldName) {
            return this[formName].fields[fieldName].message ?? false;
        },
        /**
         * Validate the given field in the given form
         * 
         * @param {string} formName
         * @param {string} fieldName
         * @returns {boolean}
         */
        validateField(formName, fieldName) {
            if (fieldName === 'privacy') {
                const requiredCheckboxes = Object.keys(this[formName].fields.privacy.groupMembers).map((statement, index) => {
                    const isRequired = this[formName].fields.privacy.groupMembers[statement].required;
                    return isRequired ? this.statements[index] : true;
                });

                return requiredCheckboxes.every(status => status === true);
            }

            if (!this[formName].fields[fieldName]) {
                return false;
            }
            if (this[formName].fields[fieldName].pristine) {
                return true;
            }

            if (!this[formName].fields[fieldName].group) {
                if (this[formName].fields[fieldName].required && !this[fieldName]) {
                    return false;
                }
                if (this[formName].fields[fieldName].pattern && !this[formName].fields[fieldName].pattern.test(this[fieldName])) {
                    return false;
                }
            } else {
                const groupMembers = this[formName].fields[fieldName].groupMembers;
                if (groupMembers) {
                    return Object.keys(groupMembers).every(memberName => {
                        if (groupMembers[memberName].required && !this[memberName]) {
                            return false;
                        }
                        return true;
                    });
                }
            }
            return true;
        },
        /**
         * Validate a field when its value changes
         * 
         * @param {string} fieldName
         * @returns {void}
         */
        handleFieldChange(formName, fieldName) {
            this[formName].fields[fieldName].pristine = false;
            this.validateField(formName, fieldName);
        },
        /**
         * Format the sort code to include hyphens
         */
        formatSortCode() {
            // Remove any non-numeric characters and keep only digits
            this.sortCode = this.sortCode.replace(/\D/g, '');

            // Insert hyphens after every 2 digits
            if (this.sortCode.length > 2) {
                this.sortCode = this.sortCode.replace(/(\d{2})(\d{0,2})(\d{0,2})/, '$1-$2-$3').replace(/-$/, '');
            }

            if (this.sortCode.length === 8) {
                document.getElementById('accountNumber').focus();
            }
        },
        /**
         * Format a date
         * 
         * @param {string} date
         * @returns {string}
         */
        formatDate(date) {
            return date ? new Date(date).toLocaleDateString('en-GB') : '';
        },
        /**
         * Format a currency amount
         * 
         * @param {number} amount
         * @returns {string}
         */
        formatCurrency(amount) {
            return amount ? amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '';
        },

        getAdyenPaymentSession(amountToPay) {
            console.log('Starting getAdyenPaymentSession with amountToPay:', amountToPay);
            this.loading = true;
            const data = {
                amountToPay,
                clubId: this.selectedClubId
            };

            return fetch(`${ajaxurl}?action=getAdyenPaymentSession`, {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=utf-8",
                    "Cache-Control": "no-cache",
                },
                body: JSON.stringify(data),
            }).then(response => {
                console.log('Received response from getAdyenPaymentSession:', response);
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.json();
            }).then(data => {
                console.log('Parsed JSON data from getAdyenPaymentSession:', data);
                if (data.success && data.session) {
                    console.log('Successfully retrieved Adyen payment session:', data.session);
                    return data.session;
                } else {
                    throw new Error(`Error getting Adyen payment session. Message from Adyen: ${data.message}`);
                }
            }).catch(error => {
                console.error('Error in getAdyenPaymentSession:', error);
                throw error;
            }).finally(() => {
                this.loading = false;
                console.log('Finished getAdyenPaymentSession');
            });
        },
        async initAdyenCheckout(amountToPay) {
            if (!this.selectedClub || !this.adyenClientKey) {
                console.error('No club selected or no Adyen API key available');
                return;
            }
            const { id, data } = await this.getAdyenPaymentSession(amountToPay);
            console.log('Payment session ID:', id);
            console.log('Payment session data:', data);
            const configuration = {
                session: {
                    id,
                    sessionData: data,
                },
                environment: 'test',
                amount: {
                    // TODO: what is this value? is it needed?
                    value: amountToPay,
                    currency: 'GBP',
                },
                locale: 'en-GB',
                countryCode: 'GB',
                clientKey: this.adyenClientKey,
                analytics: {
                    enabled: true,
                },
                onPaymentCompleted: async (result, component) => {
                    console.log('Payment completed: ', result, component);
                    await this.confirmMember();
                    console.info(result, component);
                },
                onPaymentFailed: (result, component) => {
                    console.log('Payment failed: ', result, component);
                    console.info(result, component);
                },
                onError: (error, component) => {
                    console.log('Payment error: ', error, component);
                    console.error(error.name, error.message, error.stack, component);
                }
            };

            // Create an instance of AdyenCheckout, to pass when you create an instance of Drop-in.
            const checkout = await AdyenWeb.AdyenCheckout(configuration);

            const dropinConfiguration = {
                // Required if you import individual payment methods.
                // paymentMethodComponents: [Card, PayPal, GooglePay, ApplePay, Ideal],

                // Drop-in configuration only has properties and events related to itself. Drop-in configuration cannot contain global configuration.
                onReady: () => { },
                // instantPaymentTypes: ['applepay', 'googlepay'],

                // Configuration for individual payment methods.
                paymentMethodsConfiguration: {
                    card: {
                        // onError configuration for card payments. Overrides the global configuration.
                        onError: () => { }
                    }
                }
            };

            // Create an instance of Drop-in, passing the instance of AdyenCheckout and configuration object for Drop-in.
            const dropin = new AdyenWeb.Dropin(checkout, dropinConfiguration).mount('#dropin');

            // run update in case the dropin has been mounted before but the amount has changed
            dropin.update();
        },

        getAddonById(addonId) {
            return this.addOns.find(addon => addon.id === addonId);
        },

        getDirectDebitContent() {
            if (this.selectedClub) {
                return this.directDebitContent.replace(/{clubName}/g, this.selectedClub.name);
            }
            return this.directDebitContent;
        }

        // #endregion
    }));
}); 